import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AddFilesIcon from "../../../assets/images/policies-manuals/addfiles-icon.svg";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { XIcon } from "lucide-react";
import images from "../../../assets/images";
import AddFileModal from "./AddFileModal";
import {
  getComplianceAuditFolderContents,
  updateComplianceAuditFolder,
  downloadComplianceAuditFolder,
} from "../../../service/api";
import Modal from "../../../components/modal/Modal";
import { SpinLoader, DotLoader } from "../../../components/loader/Loader";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import { useTaskStore } from "../../../store/store";
import { datesubstract } from "../../../common/utils";
import { usePermissions } from "../../../hooks/usePermission";
import { COMPLIANCE_AUDIT_DOCUMENTATION, POLICY_MODULE } from "../../../constants/modules";

interface Ifile {
  name: string;
  originalName?: string; // Added to store the original file name for S3 retrieval
  size: string;
  expiry: string;
  reminder: string;
  url: string;
  type: string;
}

const FolderView = () => {
  const { tasks, loading, addTask } = useTaskStore();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editFileModalOpen, setEditFileModalOpen] = useState(false);
  const [resMessage, setResMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editFile, setEditFile] = useState<Ifile | null>(null);
  const [editFileName, setEditFileName] = useState<string>("");
  const [editExpiry, setEditExpiry] = useState<string>("");
  const [editReminder, setEditReminder] = useState<string>("");
  const [editErrorMessage, setEditErrorMessage] = useState<string>("");
  const [newFile, setNewFile] = useState<File | null>(null);
  const [fileRemoved, setFileRemoved] = useState<boolean>(false);
  const { uploadFileToS3, progress, uploading, error } = useS3Upload();
  const [fileurl, setFileUrl] = useState<{ url: string; name: string }>({
    url: "",
    name: "",
  });
  const { getsignedurl } = S3TempUrl();
  const [files, setFiles] = useState<Ifile[]>([]);

  const { canCreate, canRead, canUpdate } = usePermissions();

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditFileModalOpen(false);
    setEditFile(null);
    setEditFileName("");
    setEditExpiry("");
    setEditReminder("");
    setEditErrorMessage("");
    setNewFile(null);
    setFileRemoved(false);
  }, []);

  const formatSize = (sizeInKB: number): string => {
    if (sizeInKB === 0) return "0 KB";
    if (sizeInKB < 1024) {
      return `${sizeInKB} KB`;
    }
    const sizeInMB = sizeInKB / 1024;
    if (sizeInMB < 1024) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInGB = sizeInMB / 1024;
    return `${sizeInGB.toFixed(2)} GB`;
  };

  const handleUpload = useCallback(
    async (
      file: File | null,
      expiryDate: string,
      reminder: string
    ) => {
      if (!file) {
        setResMessage("File cannot be empty");
        return;
      }
      setIsLoading(true);
      try {
        const uploadedFileUrl = await uploadFileToS3(file);

        // Create a more efficient payload - only send the new file
        const newFileData = {
          name: file.name,
          originalName: file.name,
          size: formatSize(file.size / 1024),
          expiry: expiryDate,
          reminder: reminder,
          url: uploadedFileUrl,
          type: file.type,
        };

        // Try a more efficient approach - send only the new file
        const fileObj = {
          files: [newFileData], // Only send the new file, not all existing files
          action: "add", // Indicate this is an add operation
        };

        const fileUpRes = await updateComplianceAuditFolder(fileObj, id);

        if (fileUpRes.data.resCode !== 200) {
          // Don't show database errors to user, just log them
          console.error("Upload failed:", fileUpRes.data.resMessage);
          setResMessage(""); // Clear any existing error message
        } else {
          const dueDate = datesubstract(
            expiryDate,
            Number(reminder.split(" ")[0])
          );
          const taskObject = {
            responsibility: "Building Occupier",
            task: file.name,
            premises: "",
            topic: "Fire System",
            reference: "Building Occupier Tasks",
            frequency: "One Time",
            fromDueDate: dueDate,
            toDueDate: expiryDate,
            furtherDetails: "File Upload",
            isRecurring: false,
          };
          await addTask(taskObject);

          // Refresh files from server to ensure we have the latest data
          await fetchFiles();
          setResMessage(""); // Clear any existing message
        }
      } catch (err: any) {
        // Don't show server errors to user, just log them
        console.error(
          "Upload error:",
          err?.response?.data?.resMessage || err?.message
        );
        setResMessage(""); // Clear any existing error message
      } finally {
        setIsLoading(false);
      }
    },
    [id, addTask]
  );

  const fetchFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getComplianceAuditFolderContents(id, {});
      if (res.data.resCode !== 200) {
        setResMessage(res.data.resMessage);
        setFiles([]);
      } else {
        const fetchedFiles = (res.data.message.files || res.data.message || []).map(
          (file: any) => ({
            ...file,
            originalName: file.originalName || file.name, // Backward compatibility
          })
        ) as Ifile[];
        setFiles(fetchedFiles);

        setResMessage(
          fetchedFiles.length === 0
            ? "No files found"
            : ""
        );
      }
    } catch (err: any) {
      setResMessage(err?.message ?? "Something went wrong");
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const handleEditFile = useCallback(async () => {
    if (!editFile || !editFileName.trim()) {
      setResMessage("File name cannot be empty");
      return;
    }
    setEditFileModalOpen(false);
    setIsLoading(true);
    let updatedFileUrl = editFile.url;
    let updatedFileType = editFile.type;
    let updatedFileSize = editFile.size;
    let updatedOriginalName = editFile.originalName || editFile.name;

    // If a new file is uploaded, upload it to S3 and get the new URL
    if (newFile) {
      try {
        updatedFileUrl = await uploadFileToS3(newFile);
        updatedFileType = newFile.type;
        updatedFileSize = newFile.size + " KB";
        updatedOriginalName = newFile.name; // Update originalName for new file
      } catch (err: any) {
        setResMessage(err?.message ?? "Failed to upload new file");
        setIsLoading(false);
        return;
      }
    } else if (fileRemoved) {
      // If the file was removed and no new file was uploaded, handle accordingly
      setResMessage("No file selected after removal");
      setIsLoading(false);
      return;
    }

    const updatedFiles = files.map((file) =>
      file.url === editFile.url
        ? {
            ...file,
            name: editFileName, // Only update the display name
            originalName: updatedOriginalName, // Preserve or update originalName
            expiry: editExpiry,
            reminder: editReminder,
            url: updatedFileUrl,
            type: updatedFileType,
            size: updatedFileSize,
          }
        : file
    );
    const fileObj = {
      files: updatedFiles,
    };
    try {
      const res = await updateComplianceAuditFolder(fileObj, id);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage || "File updated successfully");
        setFiles(updatedFiles);
      } else {
        // Don't show database errors to user, just log them
        console.error("File update failed:", res.data.resMessage);
        setResMessage(""); // Clear any existing error message
      }
    } catch (err: any) {
      // Don't show server errors to user, just log them
      console.error("File update error:", err.message);
      setResMessage(""); // Clear any existing error message
    } finally {
      setIsLoading(false);
      setEditFile(null);
      setEditFileName("");
      setEditExpiry("");
      setEditReminder("");
      setEditErrorMessage("");
      setNewFile(null);
      setFileRemoved(false);
    }
  }, [editFile, editFileName, newFile, fileRemoved, files, id]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFile = e.target.files[0];
        if (selectedFile.size > 5 * 1024 * 1024) {
          setEditErrorMessage("File size cannot be more than 5MB");
          return;
        }
        setNewFile(selectedFile);
        setEditFileName(selectedFile.name); // Automatically update file name field
        setEditErrorMessage("");
      }
    },
    []
  );

  const handleRemoveFile = useCallback(() => {
    setFileRemoved(true);
    setNewFile(null);
  }, []);

  const openEditFileModal = useCallback(
    (file: Ifile) => {
      setEditFile(file);
      setEditFileName(file.name);
      setEditExpiry(file.expiry);
      setEditReminder(file.reminder);
      setEditFileModalOpen(true);
      setNewFile(null);
      setFileRemoved(false);
    },
    []
  );

  const handleDownloadFile = useCallback(
    async (fileName: string, fileType: string) => {
      try {
        setIsLoading(true);
        const signedUrl = await getsignedurl(fileName, fileType);
        // Fetch the file content as a Blob
        const response = await fetch(signedUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch file from S3");
        }
        const blob = await response.blob();
        // Create a temporary URL for the Blob
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the Blob URL
        window.URL.revokeObjectURL(blobUrl);
      } catch (err: any) {
        setResMessage(err?.message ?? "Failed to download file");
      } finally {
        setIsLoading(false);
      }
    },
    []
  );
  useEffect(() => {
    fetchFiles();
  }, [id, fetchFiles]);

  useEffect(() => {
    if (error !== null) setResMessage(error);
  }, [error]);

  const viewFile = useCallback(async (file: Ifile) => {
    try {
      // Use originalName if available, otherwise fall back to name
      const fileName = file.originalName || file.name;
      const fileurl = await getsignedurl(fileName, file.type);
      const filobj = {
        url: fileurl,
        name: file.name, // Use display name for viewer
      };
      setFileUrl(filobj);
    } catch (err: any) {
      setResMessage("File not found");
    }
  }, []);
  return (
    <div className="px-2 py-4 mt-20">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold"></h2>
        <div className="flex gap-2">
          {canCreate(COMPLIANCE_AUDIT_DOCUMENTATION) && (
            <button
              className="flex items-center bg-teal-500 text-white px-4 py-2 rounded-full"
              onClick={() => setIsModalOpen(true)}
            >
              <img
                src={AddFilesIcon}
                alt="Add Files"
                className="w-5 h-5 mr-2"
              />
              ADD FILES
            </button>
          )}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white bg-gray-700 px-4 py-2 rounded-full"
          >
            BACK
          </button>
        </div>
      </div>
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center p-4">
            <SpinLoader />
          </div>
        ) : (
          <>
            {files.length > 0 ? (
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-100 text-gray-600 uppercase text-sm">
                  <tr>
                    <th className="p-2">Files</th>
                    <th className="p-2">File Size</th>
                    <th className="p-2">Expiry Date</th>
                    <th className="p-2">Reminder Info</th>
                    <th className="p-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-2">{file.name}</td>
                      <td className="p-2">{formatSize(parseInt(file.size))}</td>
                      <td className="p-2">{file.expiry}</td>
                      <td className="p-2">{file.reminder}</td>
                      <td className="p-2 flex justify-center gap-2">
                        {canRead(COMPLIANCE_AUDIT_DOCUMENTATION) && (
                          <button className="text-blue-500">
                            <img
                              src={images.viewIcon}
                              alt="View"
                              className="w-5 h-5"
                              onClick={() => viewFile(file)}
                            />
                          </button>
                        )}
                        {canUpdate(COMPLIANCE_AUDIT_DOCUMENTATION) && (
                          <button className="text-green-500">
                            <img
                              src={images.editIcon}
                              alt="Edit"
                              className="w-5 h-5"
                              onClick={() => openEditFileModal(file)}
                            />
                          </button>
                        )}
                          {canRead(COMPLIANCE_AUDIT_DOCUMENTATION) && (
                          <button className="text-gray-500">
                            <img
                              src={images.downloadIcon}
                              alt="Download"
                              className="w-5 h-5"
                              /* spell-checker: disable */
                              onClick={() =>
                                handleDownloadFile(
                                  file.originalName || file.name,
                                  file.type
                                )
                              }
                              /* spell-checker: enable */
                            />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="align-middle p-4">This folder is empty</p>
            )}
            {isModalOpen && (
              <AddFileModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onUpload={handleUpload}
                folderId={id ?? ""}
              />
            )}
            <Modal
              isOpen={editFileModalOpen}
              onClose={handleEditModalClose}
              closeButton={false}
            >
              <div className="bg-white rounded-lg shadow-lg w-[800px]">
                <div className="p-6 overflow-hidden">
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold">Edit File</h2>
                    <hr className="my-2 border-gray-300" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="w-full">
                      <label className="block text-gray-600 font-semibold">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        className="w-full border rounded-md p-2"
                        value={editExpiry}
                        onChange={(e) => setEditExpiry(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-600 font-semibold">
                        Reminder Days Before
                      </label>
                      <select
                        className="w-full border rounded-md p-2"
                        value={editReminder}
                        onChange={(e) => setEditReminder(e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="1 Week">1 Week</option>
                        <option value="2 Weeks">2 Weeks</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-600 font-semibold">
                      File Name
                    </label>
                    <input
                      type="text"
                      className="w-full border rounded-md p-2"
                      placeholder="Enter File Name"
                      value={editFileName}
                      onChange={(e) => setEditFileName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 font-semibold">
                      Upload File
                    </label>
                    <div className="mb-4">
                      {editFile && !fileRemoved && !newFile && (
                        <div className="flex items-center border p-2 rounded mb-2">
                          <span className="text-gray-600 flex-1">
                            {editFile.name}
                          </span>
                          <button
                            className="text-red-500"
                            onClick={handleRemoveFile}
                            title="Remove file"
                            aria-label="Remove file"
                          >
                            <XIcon className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                      {(fileRemoved || newFile || !editFile) && (
                        <div className="border-dashed border-2 p-6 text-center cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            id="editFileInput"
                            onChange={handleFileChange}
                          />
                          <label
                            htmlFor="editFileInput"
                            className="cursor-pointer"
                          >
                            {newFile ? (
                              <p className="text-gray-600">{newFile.name}</p>
                            ) : (
                              "Drag & Drop Your File Here Or Click"
                            )}
                          </label>
                          <p className="text-sm text-gray-500 mt-2">
                            Supported formats: DOCX, PDF, TXT, JPG, PNG (Max:
                            5MB)
                          </p>
                        </div>
                      )}
                    </div>
                    {editErrorMessage && (
                      <p className="text-red-600 text-sm">{editErrorMessage}</p>
                    )}
                  </div>
                </div>
                <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4">
                  <button
                    onClick={handleEditModalClose}
                    className="px-4 py-2 bg-gray-400 text-white rounded-full"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditFile}
                    className="px-4 py-2 bg-teal-500 text-white rounded-full"
                    disabled={isLoading}
                  >
                    Save
                  </button>
                </div>
              </div>
            </Modal>
            {isLoading && (
              <div
                className="absmiddle bg-[#ffffff40]"
                style={{ width: "100vw", height: "100vh", zIndex: 9999 }}
              >
                <div
                  className="absmiddle"
                  style={{ width: "100px", height: "50px" }}
                >
                  <DotLoader />
                </div>
              </div>
            )}
            {uploading && (
              <div
                className="absmiddle"
                style={{ width: "100px", height: "50px" }}
              >
                Uploading ....
              </div>
            )}
            {progress > 0 && progress < 100 && (
              <div
                className="absmiddle"
                style={{ width: "100px", height: "50px" }}
              >
                <div
                  className="relative size-40"
                  style={{ transform: "scale(0.7)" }}
                >
                  <svg
                    className="size-full -rotate-90"
                    viewBox="0 0 36 36"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      strokeDasharray: 100,
                      strokeDashoffset: 100 - progress,
                    }}
                  >
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      className="stroke-current text-gray-200 dark:text-neutral-700"
                      strokeWidth="2"
                    ></circle>
                    <circle
                      cx="18"
                      cy="18"
                      r="16"
                      fill="none"
                      className="stroke-current text-blue-600 dark:text-blue-500"
                      strokeWidth="2"
                      strokeLinecap="round"
                    ></circle>
                  </svg>
                  <div className="absolute top-1/2 start-1/2 transform -translate-y-1/2 -translate-x-1/2">
                    <span className="text-center text-2xl font-bold text-blue-600 dark:text-blue-500">
                      {progress}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {fileurl.url !== "" && (
              <div
                className="absmiddle"
                style={{
                  width: "100vw",
                  height: "100vh",
                  zIndex: 999999,
                  backgroundColor: "rgba(0,0,0,0.5)",
                }}
              >
                <XIcon
                  className="absolute left-auto top-2 z-10 cursor-pointer bg-red-500 text-white"
                  style={{ right: "115px" }}
                  onClick={() => setFileUrl({ url: "", name: "" })}
                />
                <div
                  className="absmiddle bg-white border py-9"
                  style={{
                    width: "calc(100vw - 200px)",
                    height: "100vh",
                    overflow: "auto",
                    boxSizing: "border-box",
                  }}
                >
                  <DocumentViewer file={fileurl.url} name={fileurl.name} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FolderView;
